import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import CategoryTile from "@/components/CategoryTile/CategoryTile";
import IconButton from "@/components/IconButton/IconButton";
import { useMenuData } from "@/hooks/useMenuData";
import { cartItemCount, formatGrosze } from "@/helpers/cartPricing";
import { useCartLines, useCartSubtotal } from "@/store/useCartStore";
import { ALL_MENU_TYPE_ID, billsPath, orderPaths } from "@/routes/paths";
import { useGoBack } from "@/hooks/useGoBack";
import OrderActionBar from "./components/OrderActionBar";
import styles from "./Order.module.scss";

/** Landing screen of the ordering flow: pick a menu. */
function OrderMenuTypesPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = orderPaths(restaurantId, tableId);
  const goBack = useGoBack(billsPath(restaurantId, tableId));

  const { dishes, menuTypeGroups, loading, error, reload } =
    useMenuData(restaurantId);
  const lines = useCartLines();
  const subtotal = useCartSubtotal();
  const itemCount = cartItemCount(lines);

  // A venue that files no dish under a menu type has nothing to choose
  // between, so this screen would be a single tile standing between the guest
  // and the food. Skip it and open the whole list.
  if (!loading && !error && menuTypeGroups.length === 0 && dishes.length > 0) {
    return <Navigate to={paths.menuType(ALL_MENU_TYPE_ID)} replace />;
  }

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.orderWrapper}`}>
          <div className={styles.orderHeaderContainer}>
            <div className={styles.orderHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={goBack}
                color="primary"
                variant="text"
              />
              <p className={`body-bold-large ${styles.orderTitle}`}>
                {t("order.title")}
              </p>
              <div className={styles.cartButtonWrapper}>
                <IconButton
                  iconName="las la-shopping-bag icon-big"
                  onClick={() => navigate(paths.cart)}
                  color="primary"
                  variant="text"
                />
                {itemCount > 0 ? (
                  <div className={styles.cartButtonBadge}>
                    <p
                      className={`body-bold-small ${styles.cartButtonBadgeValue}`}
                    >
                      {itemCount}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className={styles.orderContent}>
            {error ? (
              <>
                <p className={`body-medium ${styles.orderStateMessage}`}>
                  {t("order.loadError")}
                </p>
                <Button
                  onClick={reload}
                  label={t("order.button.retry")}
                  variant="outlined"
                  color="primary"
                  mobile={true}
                />
              </>
            ) : null}

            {!error && !loading && dishes.length === 0 ? (
              <p className={`body-medium ${styles.orderStateMessage}`}>
                {t("order.emptyMenu")}
              </p>
            ) : null}

            <div className={styles.categoryGrid}>
              {/* Menus cover most dishes but not all — anything filed under no
                  menu type is only reachable through this tile. */}
              {dishes.length > 0 ? (
                <CategoryTile
                  name={t("order.allDishes")}
                  subtitle={t("order.dishCount", { count: dishes.length })}
                  onClick={() => navigate(paths.menuType(ALL_MENU_TYPE_ID))}
                />
              ) : null}
              {menuTypeGroups.map((group) => (
                <CategoryTile
                  key={group.menuType.id}
                  name={group.name}
                  subtitle={t("order.dishCount", { count: group.dishes.length })}
                  onClick={() => navigate(paths.menuType(group.menuType.id))}
                />
              ))}
            </div>
          </div>
        </div>
      </Grid>

      {itemCount > 0 ? (
        <OrderActionBar
          summaryLabel={`${itemCount} × ${t("order.cart.title")}`}
          summaryValue={formatGrosze(subtotal)}
        >
          <Button
            onClick={() => navigate(paths.cart)}
            label={t("order.button.viewCart")}
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
          />
        </OrderActionBar>
      ) : null}
    </>
  );
}

export default OrderMenuTypesPage;
